import { HashRouter as BrowserRouter, Routes, Route, useLocation } from "react-router-dom";
import { useEffect } from "react";
import { Toaster } from "sonner";

import "./App.css";
import { SettingsProvider } from "./lib/SettingsContext";
import { CartProvider } from "./lib/CartContext";
import { CustomerProvider } from "./lib/CustomerContext";
import { FocalMapProvider } from "./context/FocalMapContext";
import { UploadStatusProvider } from "./context/UploadStatusContext";

import Navbar from "./components/Navbar";
import Footer from "./components/Footer";
import WhatsAppButton from "./components/WhatsAppButton";

import Home from "./pages/Home";
import About from "./pages/About";
import Flavours from "./pages/Flavours";
import FlavourDetail from "./pages/FlavourDetail";
import Gallery from "./pages/Gallery";
import GalleryCategory from "./pages/GalleryCategory";
import CakeDetail from "./pages/CakeDetail";
import Shop from "./pages/Shop";
import ShopCategory from "./pages/ShopCategory";
import ProductDetail from "./pages/ProductDetail";
import Cart from "./pages/Cart";
import Checkout from "./pages/Checkout";
import Login from "./pages/Login";
import Signup from "./pages/Signup";
import Account from "./pages/Account";
import AccountOrders from "./pages/AccountOrders";
import OrderTrack from "./pages/OrderTrack";
import Contact from "./pages/Contact";
import Blog from "./pages/Blog";
import BlogDetail from "./pages/BlogDetail";

import AdminLogin from "./pages/admin/AdminLogin";
import AdminLayout from "./pages/admin/AdminLayout";
import AdminDashboard from "./pages/admin/AdminDashboard";
import AdminFlavours from "./pages/admin/AdminFlavours";
import AdminCakes from "./pages/admin/AdminCakes";
import AdminCategories from "./pages/admin/AdminCategories";
import AdminProducts from "./pages/admin/AdminProducts";
import AdminShopCategories from "./pages/admin/AdminShopCategories";
import AdminOrders from "./pages/admin/AdminOrders";
import AdminBlogs from "./pages/admin/AdminBlogs";
import AdminInquiries from "./pages/admin/AdminInquiries";
import AdminCustomers from "./pages/admin/AdminCustomers";
import AdminSettings from "./pages/admin/AdminSettings";
import AdminMediaLibrary from "./pages/admin/AdminMediaLibrary";
import AdminMoments from "./pages/admin/AdminMoments";

function ScrollToTop() {
    const { pathname } = useLocation();
    useEffect(() => { window.scrollTo({ top: 0, behavior: "instant" }); }, [pathname]);
    return null;
}

function PublicLayout({ children }) {
    return (
        <>
            <Navbar />
            {children}
            <Footer />
            <WhatsAppButton />
        </>
    );
}

function App() {
    return (
        <FocalMapProvider>
        <UploadStatusProvider>
        <SettingsProvider>
            <CustomerProvider>
                <CartProvider>
                    <div className="App">
                        <BrowserRouter>
                            <ScrollToTop />
                            <Toaster position="bottom-right" richColors />
                            <Routes>
                                <Route path="/" element={<PublicLayout><Home /></PublicLayout>} />
                                <Route path="/about" element={<PublicLayout><About /></PublicLayout>} />
                                <Route path="/flavours" element={<PublicLayout><Flavours /></PublicLayout>} />
                                <Route path="/flavours/:slug" element={<PublicLayout><FlavourDetail /></PublicLayout>} />
                                {/* Gallery — bespoke inquiry-only catalog (3 levels: categories → portfolio → cake+form) */}
                                <Route path="/gallery" element={<PublicLayout><Gallery /></PublicLayout>} />
                                <Route path="/gallery/:category" element={<PublicLayout><GalleryCategory /></PublicLayout>} />
                                <Route path="/gallery/:category/:slug" element={<PublicLayout><CakeDetail /></PublicLayout>} />
                                {/* Shop — online-purchasable products (categories → products → detail) */}
                                <Route path="/shop" element={<PublicLayout><Shop /></PublicLayout>} />
                                <Route path="/shop/product/:slug" element={<PublicLayout><ProductDetail /></PublicLayout>} />
                                <Route path="/shop/:category" element={<PublicLayout><ShopCategory /></PublicLayout>} />
                                <Route path="/cart" element={<PublicLayout><Cart /></PublicLayout>} />
                                <Route path="/checkout" element={<PublicLayout><Checkout /></PublicLayout>} />
                                {/* Customer accounts + tracking */}
                                <Route path="/login" element={<PublicLayout><Login /></PublicLayout>} />
                                <Route path="/signup" element={<PublicLayout><Signup /></PublicLayout>} />
                                <Route path="/account" element={<PublicLayout><Account /></PublicLayout>} />
                                <Route path="/account/orders" element={<PublicLayout><AccountOrders /></PublicLayout>} />
                                <Route path="/track/:token" element={<PublicLayout><OrderTrack /></PublicLayout>} />
                                <Route path="/contact" element={<PublicLayout><Contact /></PublicLayout>} />
                                <Route path="/blog" element={<PublicLayout><Blog /></PublicLayout>} />
                                <Route path="/blog/:slug" element={<PublicLayout><BlogDetail /></PublicLayout>} />

                                <Route path="/admin/login" element={<AdminLogin />} />
                                <Route path="/admin" element={<AdminLayout />}>
                                    <Route index element={<AdminDashboard />} />
                                    <Route path="categories" element={<AdminCategories />} />
                                    <Route path="cakes" element={<AdminCakes />} />
                                    <Route path="products" element={<AdminProducts />} />
                                    <Route path="shop-categories" element={<AdminShopCategories />} />
                                    <Route path="orders" element={<AdminOrders />} />
                                    <Route path="customers" element={<AdminCustomers />} />
                                    <Route path="flavours" element={<AdminFlavours />} />
                                    <Route path="blogs" element={<AdminBlogs />} />
                                    <Route path="inquiries" element={<AdminInquiries />} />
                                    <Route path="settings" element={<AdminSettings />} />
                                    <Route path="media" element={<AdminMediaLibrary />} />
                                    <Route path="moments" element={<AdminMoments />} />
                                </Route>
                            </Routes>
                        </BrowserRouter>
                    </div>
                </CartProvider>
            </CustomerProvider>
        </SettingsProvider>
        </UploadStatusProvider>
        </FocalMapProvider>
    );
}

export default App;
